import { memo, useEffect, useState } from "react";
import type { BufferGeometry } from "three";
import { getCachedFrameGeometry, peekFrameGeometry } from "./geometryCache";
import { getFrameMicroSurfaceTexture } from "./frameMicroTexture";
import { MIRROR_FRAME_DEPTH } from "./mirrorConstants";
import type { FrameShape } from "./types";

/**
 * Procedural frame mesh — extruded ring built from `state.shape + (w, h)`.
 *
 * Replaces the previous GLB-backed pipeline. The 2D contour comes from
 * `makeFrameGeometry` (rectangle / ellipse / stadium); extrusion depth is
 * always `MIRROR_FRAME_DEPTH`. The geometry's local Z runs from 0 to depth,
 * so we shift the mesh so its **front face** lands at `apertureFrontZ` —
 * matching where the portal glass sits.
 *
 * No LED-emissive surfaces: the LED visualization (bar tubes / point spheres /
 * wall text) is drawn inside the portal by the parent scene, not on the frame
 * body itself.
 *
 * Gold / metallics: physical metal + micro map. Black & white: dielectric
 * powder-coat (metalness 0, high rough, no clearcoat).
 */
function frameIsBlack(frameColor: string): boolean {
  return frameColor.trim().toLowerCase() === "#111111";
}

/** CPB maps “White” to `#e8e8e8`; dev panel uses `#ffffff` — both are matte white in the viewer. */
function frameIsWhite(frameColor: string): boolean {
  const h = frameColor.trim().toLowerCase();
  return h === "#ffffff" || h === "#e8e8e8";
}

/**
 * CPB “Gold” is `#c9a227` — olive/flat as a PBR base; without spec it reads
 * mustard. Slightly warmer display albedo keeps the same mapping as the rest
 * of the app, but matches how gold looks under specular in product shots.
 */
const CPB_GOLD = "#c9a227";
const GOLD_VIEWER_ALBEDO = "#e9c86c";
/** CPB black `#111111` matches canvas clear `#111` — lift slightly so the rim reads against bg */
const BLACK_VIEWER_ALBEDO = "#1c1c1c";

function frameSurfaceColor(frameColor: string): string {
  if (frameIsBlack(frameColor)) return BLACK_VIEWER_ALBEDO;
  if (frameIsWhite(frameColor)) return "#ffffff";
  if (frameColor.trim().toLowerCase() === CPB_GOLD) return GOLD_VIEWER_ALBEDO;
  return frameColor;
}

/**
 * Dim version of the frame surface colour for the inner cavity-facing
 * wall. Same hue, ~22 % of the brightness. Keeps the frame reading as
 * having visible thickness on the inside edge without the PBR
 * env-map / specular hot-spots that an actual metal would produce.
 */
function dimSurfaceColor(hex: string, factor = 0.22): string {
  const m = hex.replace(/^#/, "");
  if (m.length !== 6) return hex;
  const r = Math.round(parseInt(m.slice(0, 2), 16) * factor);
  const g = Math.round(parseInt(m.slice(2, 4), 16) * factor);
  const b = Math.round(parseInt(m.slice(4, 6), 16) * factor);
  const pad = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
  return `#${pad(r)}${pad(g)}${pad(b)}`;
}

export const ProcFrameMesh = memo(function ProcFrameMesh({
  shape,
  innerW,
  innerH,
  apertureFrontZ,
  frameColor,
  envMapIntensity,
}: {
  shape: FrameShape;
  innerW: number;
  innerH: number;
  apertureFrontZ: number;
  frameColor: string;
  envMapIntensity: number;
}) {
  /**
   * Frame geometry comes from a session-shared LRU cache. On a cache hit
   * (every shape/size the user has visited at least once) we read the
   * `BufferGeometry` synchronously — no animation glitch. On a cache miss
   * (first visit to a `shape × innerW × innerH` combo) the actual build
   * (`makeFrameGeometry` + `toCreasedNormals`) is ~30–80 ms; we defer it
   * to the next `requestAnimationFrame` so the React render commits with
   * the previously held geometry, the orbit camera keeps animating, and
   * the new geometry swaps in once it's ready.
   *
   * The 1-frame visual mismatch (old frame outline + new sections) is
   * acceptable — it's bounded to ≤16 ms vs the previous synchronous freeze.
   * Cache disposal happens via LRU eviction / `clearGeometryCache()` in
   * `MirrorGroup`'s unmount; we never `dispose()` here because the cache
   * may still hand out the same buffer on the next state update.
   */
  const [geo, setGeo] = useState<BufferGeometry | null>(() =>
    peekFrameGeometry(shape, innerW, innerH),
  );

  useEffect(() => {
    const peeked = peekFrameGeometry(shape, innerW, innerH);
    if (peeked) {
      setGeo(peeked);
      return;
    }
    let cancelled = false;
    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      setGeo(getCachedFrameGeometry(shape, innerW, innerH));
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [shape, innerW, innerH]);

  const z = apertureFrontZ - MIRROR_FRAME_DEPTH;

  const black = frameIsBlack(frameColor);
  const white = frameIsWhite(frameColor);
  const matte = black || white;
  const surfaceColor = frameSurfaceColor(frameColor);
  const micro = getFrameMicroSurfaceTexture();

  /* Matte: dampen IBL so white/black don’t read as metal. Metal: defaults only (no anisotropy — avoids stretched HDRI streaks). */
  const envAtten = matte ? (white ? 0.038 : 0.14) : 1;

  if (!geo) return null;
  /**
   * Frame is split into two material groups by `classifyFrameFaces` in
   * `geometry.ts`:
   *   - material-0 — outer side walls + top/bottom caps. Keeps the full
   *     metal PBR model (envMap, clearcoat) so gold reads as gold from
   *     outside.
   *   - material-1 — inner side walls facing into the cavity. Caused a
   *     bright envMap hot-spot on the inside of the frame at certain
   *     camera angles. Zeroed envMapIntensity + low metalness + no
   *     clearcoat so the inner edge reads as flat gold tone with the
   *     same diffuse colour but no mirror reflection.
   */
  return (
    <mesh geometry={geo} position={[0, 0, z]}>
      <meshPhysicalMaterial
        attach="material-0"
        color={surfaceColor}
        metalness={matte ? 0 : 0.88}
        /**
         * Roughness raised from 0.29 → 0.45 on metal frames. The earlier
         * value was glossy enough to mirror individual HDRI panels (the
         * `studio` preset has coloured RGB LED panels on its virtual
         * ceiling/walls) as sharp green / red specular hot-spots on the
         * curved gold rim. 0.45 spreads each reflection across a larger
         * surface area so the panels blur together into a smooth
         * golden sheen — same metal tonality, no chromatic glints.
         */
        roughness={matte ? (white ? 0.992 : 0.93) : 0.45}
        roughnessMap={matte ? undefined : micro}
        bumpMap={matte ? undefined : micro}
        bumpScale={matte ? 0 : 0.0054}
        clearcoat={matte ? 0 : 0.44}
        clearcoatRoughness={matte ? 1 : 0.16}
        envMapIntensity={envMapIntensity * envAtten}
        specularIntensity={matte ? (white ? 0.2 : 0.45) : 0.8}
        specularColor={matte ? "#ffffff" : "#fff7e4"}
      />
      {/*
        Inner wall facing into the mirror cavity. Pure matte PBR — no
        metalness, full roughness, env-map intensity zeroed. That kills
        HDRI reflections (which produced the warehouse / studio hot
        spots) but still lets direct lights + ambient softly modulate
        the surface, so the inner edge reads as real geometry rather
        than the flat / cartoonish look unlit `MeshBasicMaterial`
        produced. Colour is the dim (~22 %) hue of the frame so the
        inner sliver keeps a tonal hint without competing with the
        bright outer rim.
      */}
      <meshPhysicalMaterial
        attach="material-1"
        color={dimSurfaceColor(surfaceColor)}
        metalness={0}
        roughness={1}
        envMapIntensity={0}
        clearcoat={0}
        specularIntensity={0}
      />
    </mesh>
  );
});
